import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button, Card, Input, useToast } from '@caruso/ui';
import { usersService, User, UserRole } from '@/services/users.service';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  UserCog,
  Shield,
  Calendar,
} from 'lucide-react';
import { cn } from '@caruso/ui';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function UsersPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.getAll(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersService.delete(id),
    onSuccess: () => {
      toast({
        title: 'Usuário excluído',
        description: 'Usuário removido com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => {
      toast({
        title: 'Erro ao excluir',
        description: 'Não foi possível excluir o usuário',
        variant: 'destructive',
      });
    },
  });

  const handleDelete = async (id: string, userName: string) => {
    if (!confirm(`Tem certeza que deseja excluir o usuário "${userName}"?`)) return;
    deleteMutation.mutate(id);
  };

  const filteredUsers = users?.filter((user) => {
    const searchLower = search.toLowerCase();
    return (
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)
    );
  });

  const getRoleBadge = (role: UserRole) => {
    if (role === UserRole.ADMIN) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-brand-gold/20 text-brand-gold border border-brand-gold/30">
          <Shield className="w-3 h-3" />
          Administrador
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
        <UserCog className="w-3 h-3" />
        Usuário
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-brand-smoke rounded w-48"></div>
          <div className="h-32 bg-brand-smoke rounded"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-brand-smoke rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white">Usuários do Sistema</h1>
          <p className="text-brand-ivory/80 mt-2 text-lg">
            Gerencie usuários e suas permissões de acesso
          </p>
        </div>
        <Button
          onClick={() => navigate('/app/usuarios/novo')}
          className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90 font-semibold shadow-lg"
        >
          <Plus className="w-5 h-5 mr-2" />
          Novo Usuário
        </Button>
      </div>

      {/* Search */}
      <Card className="p-6 bg-brand-smoke border-brand-gold/20">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-gold/50" />
          <Input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 text-base"
          />
        </div>
      </Card>

      {/* Users List */}
      <div className="space-y-4">
        {filteredUsers && filteredUsers.length > 0 ? (
          filteredUsers.map((user) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="p-6 bg-brand-smoke border-brand-gold/20 hover:border-brand-gold/40 transition-all">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-gold to-yellow-600 flex items-center justify-center shadow-lg">
                        <span className="text-white font-bold text-lg">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold text-white truncate">{user.name}</h3>
                        <p className="text-base text-brand-ivory/70 truncate">{user.email}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 mt-4">
                      {getRoleBadge(user.role)}

                      {user.hasCalendar && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
                          <Calendar className="w-3 h-3" />
                          Agenda Habilitada
                        </span>
                      )}

                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-brand-smoke/30 text-brand-ivory/70">
                        Criado em {format(new Date(user.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/app/usuarios/${user.id}`)}
                      className="border-brand-gold/30 text-brand-gold hover:bg-brand-gold/10"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(user.id, user.name)}
                      disabled={deleteMutation.isPending}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Excluir
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))
        ) : (
          <Card className="p-12 bg-brand-smoke border-brand-gold/20">
            <div className="text-center">
              <UserCog className="w-16 h-16 mx-auto mb-4 text-brand-gold/30" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {search ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
              </h3>
              <p className="text-brand-ivory/60 mb-6">
                {search
                  ? 'Tente ajustar os termos da busca'
                  : 'Comece adicionando seu primeiro usuário'}
              </p>
              {!search && (
                <Button
                  onClick={() => navigate('/app/usuarios/novo')}
                  className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Adicionar Usuário
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
